import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Container, Grid } from "@mui/joy";
import { getApplicants } from "../../../apiconfig/api";
import NoCandidates from "./NoCandidates";
import ScheduleMeetingButton from "./ScheduleMeetingButton";
import CandidateList from "components/common/CandidateList";
import SelectedCandidateListHeader from "./SelectedCandidateListHeader";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { useApplicants } from "context/ApplicantsContext";
import { useSession } from 'context/SessionContext';

const CandidatesSelected: React.FC = () => {
  const navigate = useNavigate();
  const { sessionEmployerId, isLoggedIn } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [removedApplicantIds, setRemovedApplicantIds] = useState<Set<string>>(new Set());
  
  const { setApplicantIds, removeApplicantId: removeFromContext } = useApplicants();

  const applicantIdsParam = searchParams.get('applicantIds');
  const selectedApplicantIds = useMemo(
    () => applicantIdsParam ? applicantIdsParam.split(',').filter(id => id.trim()) : [],
    [applicantIdsParam]
  );

  // Sync URL params with PeopleContext
  useEffect(() => {
    setApplicantIds(selectedApplicantIds);
  }, [selectedApplicantIds, setApplicantIds]);

  // Fetch function for infinite scroll hook - using a stable reference based on initial IDs
  const initialApplicantIds = useMemo(() => selectedApplicantIds, [applicantIdsParam]);
  
  const fetchApplicants = useCallback(async (page: number, per_page: number) => {
    return await getApplicants(initialApplicantIds, page, per_page);
  }, [initialApplicantIds]);

  // Infinite scroll hook for people
  const {
    data: applicants,
    loading,
    error,
    hasMore,
    loadMore,
    total: totalCandidates
  } = useInfiniteScroll({
    fetchFunction: fetchApplicants,
    perPage: 5,
    enabled: initialApplicantIds.length > 0
  });

  // Filter people based on removed IDs
  const filteredApplicants = useMemo(() => {
    return applicants.filter((applicant: any) => !removedApplicantIds.has(applicant.uuid));
  }, [applicants, removedApplicantIds]);

  // Adjusted total count
  const filteredTotalCandidates = totalCandidates - removedApplicantIds.size;

  // Reset removed IDs when the URL param changes (new selection)
  useEffect(() => {
    setRemovedApplicantIds(new Set());
  }, [applicantIdsParam]);
  
  const handlePlayerVisibilityChange = (videoId: string, isVisible: boolean) => {
    if (isVisible) {
      setActiveVideoId(videoId);
    }
  };

  // Custom function to remove a person ID - filters locally without refetching
  const removeApplicantId = (idToRemove: string) => {
    // Add to removed set for immediate UI update
    setRemovedApplicantIds(prev => new Set(prev).add(idToRemove));
    
    // Update context
    removeFromContext(idToRemove);
    
    // Check if all people have been removed
    const remainingCount = filteredApplicants.length - 1; // -1 for the one being removed
    
    if (remainingCount === 0) {
      // If no people left, go back to people page
      navigate('/hiring/candidates');
    } else {
      // Update URL query params with remaining person IDs (for persistence)
      const updatedIds = selectedApplicantIds.filter(id => id !== idToRemove);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('applicantIds', updatedIds.join(','));
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  const nextStepUrl = () => {
    const applicantIdsParam = selectedApplicantIds.join(',');
    return `/hiring/meetings/new?applicantIds=${applicantIdsParam}`;
  };

  const handleSchedule = () => {
    if (sessionEmployerId) {
      navigate(nextStepUrl());
    } else {
      navigate(`/auth/signup?newEmployer=true&redirect=${nextStepUrl()}`);
    }
  };

  const handleBackToCandidates = () => {
    // Check if the previous page is from geekapp.co domain
    const referrer = document.referrer;
    const isFromGeekApp = referrer && (
      referrer.includes('geekapp.co') || 
      referrer.includes('localhost') || 
      referrer.includes('localhost:3002') || 
      referrer.includes('127.0.0.1')
    );
    
    if (isFromGeekApp) {
      // If coming from our app, use browser back
      navigate(-1);
    } else {
      // If coming from external source or no referrer, go to people page
      navigate('/hiring/candidates');
    }
  };

  return (
    <Box sx={{ 
      minHeight: "100vh", 
      bgcolor: "#f4f4f4",
      display: "flex",
      flexDirection: "column"
    }}>
      <Box sx={{ flex: 1, pb: 4 }}>
        <Container maxWidth="lg">
          <Grid xs={12} sx={{ maxWidth: { xs: "600px", md: "none" }, mx: { xs: "auto", md: 0 } }}>

            {selectedApplicantIds.length === 0 ? (
              <NoCandidates handleBackToCandidates={handleBackToCandidates} />
            ) : (
              <CandidateList 
                candidates={filteredApplicants} 
                activeVideoId={activeVideoId || ''}
                muted={muted}
                setMuted={setMuted}
                playing={playing}
                setPlaying={setPlaying}
                onVisibilityChange={handlePlayerVisibilityChange}
                loading={loading}
                error={error || undefined}
                hasMore={hasMore}
                onLoadMore={loadMore}
                totalCandidates={filteredTotalCandidates}
                enableZipcodeFilter={true}
                enableRolesFilter={false}
                enableEmployerPositionsFilter={true}
                customFilterHandler={undefined}
                customCurrentEmployerPositions={undefined}
                alternateHeader={
                  <SelectedCandidateListHeader
                    handleBackToCandidates={handleBackToCandidates}
                    handleSchedule={handleSchedule}
                  />
                }
                alternateCallToActionButton={
                  <ScheduleMeetingButton selectedApplicantIds={selectedApplicantIds} onClick={handleSchedule} />
                }
                onRemoveApplicant={removeApplicantId}
              />
            )}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default CandidatesSelected;